import React, { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { usePipPlayerStore } from '@/stores/pip-player.store';
import { useEventFeedStore } from '@/stores/event-feed.store';
import { BoundingBoxData, EventPlaybackRange } from '@/types/video-player';
import { FullSource } from '@/types/source';
import { StreamingMode } from '@/types/streaming';
import { getVideoElementFromRef } from '@/utils/video-player';
import { VideoBoundingBox } from './VideoBoundingBox';
import { VideoCropper } from './VideoCropper';
import { VideoMagnifier } from './VideoMagnifier';
import { VideoZoomLockHint } from './VideoZoomLockHint';
import { VideoPlayerControls } from './VideoPlayerControls';
import { VideoPlayerHeader } from './VideoPlayerHeader';
import { VideoProgressBar } from './VideoProgressBar';
import { VideoErrorState } from './VideoErrorState';
import { VideoGapState } from './VideoGapState';
import { WebRTCVideo } from './WebRTCVideo';
import { VideoLoadingState } from './VideoLoadingState';
import { VideoBufferingOverlay } from './VideoBufferingOverlay';
import { ReactPlayerVideo } from './ReactPlayerVideo';
import { CameraHlsVideo } from './CameraHlsVideo';
import { SkipIndicator } from './SkipIndicator';
import { useVideoBuffering } from './hooks/useVideoBuffering';
import {
    VideoPlayerProvider,
    useVideoPlayerContext,
} from './VideoPlayerProvider';

interface VideoPlayerProps {
    boundingBoxProps?: BoundingBoxData;
    controls?: boolean;
    crop?: boolean;
    defaultLoop?: boolean;
    fullScreenTitle?: string;
    onDurationChange?: (duration: number) => void;
    onTimeUpdate?: (currentTime: number) => void;
    onPlayingChange?: (isPlaying: boolean) => void;
    onLoopChange?: (loopEnabled: boolean) => void;
    onSeeked?: () => void;
    src: string;
    startTime?: number;
    source?: FullSource;
    initialMode?: StreamingMode;
    eventRange?: EventPlaybackRange;
}

export const VideoPlayer: React.FC<VideoPlayerProps> = (props) => {
    // A full-size player and the PiP player must never play audio at once, so
    // mounting any full player pauses the PiP. The PiP itself uses
    // VideoPlayerProvider directly (not this component), so it never self-pauses.
    useEffect(() => {
        usePipPlayerStore.getState().requestPause();
    }, []);

    return (
        <VideoPlayerProvider
            boundingBoxProps={props.boundingBoxProps}
            src={props.src}
            source={props.source}
            startTime={props.startTime}
            initialMode={props.initialMode}
            defaultLoop={props.defaultLoop}
            onDurationChange={props.onDurationChange}
            onTimeUpdate={props.onTimeUpdate}
            eventRange={props.eventRange}
            onPlayingChange={props.onPlayingChange}
            onLoopChange={props.onLoopChange}
            onSeeked={props.onSeeked}
            cropEnabled={props.crop}
        >
            <VideoPlayerLayout
                boundingBoxProps={props.boundingBoxProps}
                controls={props.controls}
                fullScreenTitle={props.fullScreenTitle}
                startTime={props.startTime}
                source={props.source}
            />
        </VideoPlayerProvider>
    );
};

const PROGRESS_BAR_AUTO_SHOW_DURATION_MS = 5000;
const EXPANDED_VIDEO_MAX_WIDTH = 'max-w-[min(1500px,calc((100vh-280px)*16/9))]';

interface VideoPlayerLayoutProps {
    boundingBoxProps?: BoundingBoxData;
    controls?: boolean;
    fullScreenTitle?: string;
    startTime?: number;
    source?: FullSource;
}

function VideoPlayerLayout({
    boundingBoxProps,
    controls = false,
    fullScreenTitle,
    startTime,
    source,
}: VideoPlayerLayoutProps) {
    const ctx = useVideoPlayerContext();
    const isBuffering = useVideoBuffering();
    const [showVideoProgressBar, setShowVideoProgressBar] = useState(false);
    const progressBarTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const {
        activeMediaRef,
        containerRef,
        expandedRootRef,
        expanded,
        toggleExpanded,
        hovering,
        setHovering,
        streaming,
        showErrorState,
        streamUnavailable,
        skipIndicator,
        togglePlay,
        croppingMode,
        zoomed,
        emitter,
    } = ctx;

    const handleVideoClick = () => {
        if (croppingMode) return;
        togglePlay();
    };

    useEffect(() => {
        const showProgressBar = () => {
            setShowVideoProgressBar(true);
            if (progressBarTimeoutRef.current) {
                clearTimeout(progressBarTimeoutRef.current);
            }
            progressBarTimeoutRef.current = setTimeout(() => {
                setShowVideoProgressBar(false);
            }, PROGRESS_BAR_AUTO_SHOW_DURATION_MS);
        };

        const unsubscribe = emitter.on('ready', showProgressBar);

        return () => {
            unsubscribe();
            if (progressBarTimeoutRef.current) {
                clearTimeout(progressBarTimeoutRef.current);
            }
        };
    }, [emitter]);

    const mediaPlaybackStart = ctx.loopTimeRange?.playbackStart ?? startTime;

    useEffect(() => {
        if (
            mediaPlaybackStart == null ||
            streaming.streamBeginTimestamp !== null
        ) {
            return;
        }
        const videoElement = getVideoElementFromRef(activeMediaRef);
        if (videoElement) {
            ctx.loopSeekingRef.current = true;
            videoElement.currentTime = mediaPlaybackStart;
        }
    }, [
        mediaPlaybackStart,
        streaming.videoSrc,
        activeMediaRef,
        streaming.streamBeginTimestamp,
    ]);

    useEffect(() => {
        if (!expanded) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                // When zoomed, Escape exits zoom first (handled by the controls
                // key handler); only collapse fullscreen once zoom is off.
                if (zoomed) return;
                toggleExpanded();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [expanded, toggleExpanded, zoomed]);

    // A fullscreen player must cover the event feed panel, so report the
    // expanded state and let the layout hide the panel while it is active.
    useEffect(() => {
        if (!expanded) return;
        const { setPlayerIsFullscreen } = useEventFeedStore.getState();
        setPlayerIsFullscreen(true);
        return () => setPlayerIsFullscreen(false);
    }, [expanded]);

    const awaitingInitialPosition =
        ctx.awaitingInitialPosition &&
        !showErrorState &&
        Boolean(streaming.videoSrc);

    const shouldShowProgressBar =
        (hovering || showVideoProgressBar) && !awaitingInitialPosition;

    const rootClassName = expanded
        ? 'fixed inset-0 z-[60] bg-black-100 flex flex-col items-center justify-center px-8 gap-6'
        : 'flex flex-col items-center w-full';

    const videoContainerClassName = expanded
        ? 'relative w-full cursor-pointer aspect-video'
        : 'relative w-full cursor-pointer bg-black-100 rounded-lg aspect-video';

    const videoWrapperClassName = expanded
        ? cn('w-full', EXPANDED_VIDEO_MAX_WIDTH)
        : 'w-full';

    const renderVideoContent = () => {
        if (showErrorState) {
            return (
                <VideoErrorState
                    hasArchive={streaming.hasArchive}
                    canGoLive={streaming.canGoLive}
                />
            );
        }

        if (streaming.shouldUseWebRTC) {
            return <WebRTCVideo />;
        }

        if (!streaming.videoSrc) {
            return <VideoLoadingState />;
        }

        if (ctx.isCamera) {
            return <CameraHlsVideo />;
        }

        return <ReactPlayerVideo />;
    };

    return (
        <div
            ref={expanded ? expandedRootRef : undefined}
            className={rootClassName}
        >
            {expanded && (
                <VideoPlayerHeader
                    title={fullScreenTitle}
                    onClose={toggleExpanded}
                />
            )}
            <div className={videoWrapperClassName}>
                <div className="flex flex-col w-full">
                    <div
                        className="relative overflow-visible"
                        style={{ isolation: 'isolate' }}
                    >
                        <div
                            className={cn(
                                videoContainerClassName,
                                'relative z-10',
                                croppingMode && 'cursor-crosshair',
                            )}
                            ref={containerRef}
                            onMouseEnter={() => setHovering(true)}
                            onMouseLeave={() => setHovering(false)}
                            onClick={handleVideoClick}
                            onContextMenu={(e) => e.preventDefault()}
                        >
                            <div
                                className={videoContainerClassName}
                                style={
                                    zoomed ? { cursor: 'zoom-in' } : undefined
                                }
                            >
                                {renderVideoContent()}
                                {streamUnavailable && <VideoGapState />}
                            </div>

                            {(isBuffering || awaitingInitialPosition) && (
                                <VideoBufferingOverlay />
                            )}

                            {boundingBoxProps?.drawBoxes && !streaming.live && (
                                <VideoBoundingBox
                                    sourceType={source?.type}
                                    pictureRect={ctx.pictureRect}
                                    timeBase={
                                        ctx.streaming.streamBeginTimestamp ??
                                        undefined
                                    }
                                    frames={boundingBoxProps.frames}
                                    videoRef={activeMediaRef}
                                    emitter={ctx.emitter}
                                />
                            )}
                            <VideoZoomLockHint />
                            <VideoMagnifier />
                            {!expanded && (
                                <VideoProgressBar
                                    overlay={true}
                                    visible={
                                        shouldShowProgressBar && !croppingMode
                                    }
                                />
                            )}
                            {skipIndicator.isVisible &&
                                skipIndicator.direction && (
                                    <SkipIndicator
                                        direction={skipIndicator.direction}
                                        accumulatedSeconds={
                                            skipIndicator.accumulatedSeconds
                                        }
                                        isVisible={skipIndicator.isVisible}
                                    />
                                )}
                            <VideoCropper />
                        </div>
                        {!expanded && controls && (
                            <div className="relative z-10 mt-4">
                                <VideoPlayerControls expanded={false} />
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {expanded && (
                <div
                    className={cn(
                        'w-full flex flex-col flex-shrink-0 gap-4',
                        EXPANDED_VIDEO_MAX_WIDTH,
                    )}
                >
                    <VideoProgressBar
                        overlay={false}
                        visible={!awaitingInitialPosition}
                    />
                    <VideoPlayerControls expanded={true} />
                </div>
            )}
        </div>
    );
}
